import React, { useState, useEffect } from 'react';
import { Play, Sparkles, TrendingUp } from 'lucide-react';
import axios from 'axios';

const FeaturedVideoSection = () => {
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchVideo();
  }, []);

  const fetchVideo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/featured-video`);
      if (response.data && response.data.is_active) {
        setVideoData(response.data);
      }
    } catch (error) {
      console.error('Error fetching featured video:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const getEmbedUrl = () => {
    if (!videoData) return null;
    const videoId = extractVideoId(videoData.video_url);
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0` : null;
  };

  if (loading || !videoData) {
    return null;
  }

  const embedUrl = getEmbedUrl();
  if (!embedUrl) return null;

  return (
    <div className="relative my-8 overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-0 left-1/2 w-64 h-64 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      {/* Content */}
      <div className="relative max-w-6xl mx-auto px-4 py-10">
        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Left Side - Text Content */}
          <div className="lg:w-2/5 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full mb-4">
              <Sparkles className="w-4 h-4 text-yellow-300 animate-pulse" />
              <span className="text-white text-sm font-semibold">Featured Product</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3 leading-tight">
              {videoData.title || 'Discover Our Products'}
            </h2>
            
            {videoData.description && (
              <p className="text-base text-gray-200 mb-6">
                {videoData.description}
              </p>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-3">
              {videoData.explore_more_url ? (
                <a 
                  href={videoData.explore_more_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full font-semibold text-sm text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition duration-300 overflow-hidden inline-flex items-center gap-2"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <Play className="w-4 h-4" />
                    Explore More
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 opacity-0 group-hover:opacity-100 transition duration-300"></div>
                </a>
              ) : (
                <button 
                  disabled
                  className="px-6 py-2.5 bg-gray-400 rounded-full font-semibold text-sm text-white cursor-not-allowed opacity-50"
                >
                  <span className="flex items-center gap-2">
                    <Play className="w-4 h-4" />
                    Explore More
                  </span>
                </button>
              )}
              
              {videoData.learn_more_url ? (
                <a 
                  href={videoData.learn_more_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-2.5 bg-white/10 backdrop-blur-md rounded-full font-semibold text-sm text-white border-2 border-white/20 hover:bg-white/20 transform hover:scale-105 transition duration-300 inline-block"
                >
                  Learn More
                </a>
              ) : (
                <button 
                  disabled
                  className="px-6 py-2.5 bg-gray-400/50 rounded-full font-semibold text-sm text-white border-2 border-gray-400/20 cursor-not-allowed opacity-50"
                >
                  Learn More
                </button>
              )}
            </div>
          </div>

          {/* Right Side - Video */}
          <div className="lg:w-3/5">
            <div className="relative group">
              {/* Glow Effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 rounded-xl blur-xl opacity-60 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
              
              {/* Video Frame */}
              <div className="relative bg-black rounded-xl overflow-hidden shadow-2xl">
                <div className="aspect-video">
                  <iframe
                    src={embedUrl}
                    title={videoData.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full opacity-20 blur-xl"></div>
              <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full opacity-20 blur-xl"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-50 dark:from-gray-900 to-transparent pointer-events-none"></div>
    </div>
  );
};

export default FeaturedVideoSection;
